//Bibliotecas
import React from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter, Switch } from 'react-router-dom';
//Componentes Material
import { createMuiTheme } from '@material-ui/core/styles';
import ThemeProvider from '@material-ui/styles/ThemeProvider';
//Componentes
import ConditionalRoute from './components/ConditionalRoute';
//Controllers
import LoginController from './controllers/LoginController';
import PermissaoController, { modulos, perfilAcesso } from './controllers/PermissaoController';
//Páginas
import LoginPage from './pages/LoginPage';
import RecuperarSenhaPage from './pages/RecuperarSenhaPage';
import ResetarSenhaPage from './pages/ResetarSenhaPage';
import DashboardPage from './pages/DashboardPage';
import EmpresaListPage from './pages/EmpresaListPage';
import EmpresaFormPage from './pages/EmpresaFormPage';
import ClienteListPage from './pages/ClienteListPage';
import ClienteFormPage from './pages/ClienteFormPage';
import AvaliadorListPage from './pages/AvaliadorListPage';
import AvaliadorFormPage from './pages/AvaliadorFormPage';
import AdministradorListPage from './pages/AdministradorListPage';
import AdministradorFormPage from './pages/AdministradorFormPage';
import QuestionarioListPage from './pages/QuestionarioListPage';
import QuestionarioFormPage from './pages/QuestionarioFormPage';
import AvaliacaoListPage from './pages/AvaliacaoListPage';
import AvaliacaoAddPage from './pages/AvaliacaoAddPage';
import AvaliacaoEditPage from './pages/AvaliacaoEditPage';
import ResultadoFilterPage from './pages/ResultadoFilterPage';
import ResultadoShowPage from './pages/ResultadoShowPage';
import LoadingAppPage from './pages/LoadingAppPage';

const theme = createMuiTheme({
    palette: {
        background: {
            default: 'rgba(63, 81, 181, 0.1)'
        }
    }
});

/**
 * Aplicação
 */
class App extends React.Component {
    constructor(props) {
        super(props);
        //Bindings
        this.handleLogin = this.handleLogin.bind(this);
        this.handleLogout = this.handleLogout.bind(this);
        //Controllers
        this.loginController = new LoginController();
        this.permissaoController = new PermissaoController();
        //Estado inicial
        this.state = {
            carregando: true,
            autenticado: false,
            permiteCadastrarAdmin: false,
            perfilCliente: false,
            empresa: null,
            usuario: null,
            permissoes: {
                [modulos.EMPRESAS       ]: false,
                [modulos.CLIENTES       ]: false,
                [modulos.AVALIADORES    ]: false,
                [modulos.ADMINISTRADORES]: false,
                [modulos.QUESTIONARIOS  ]: false,
                [modulos.AVALIACOES     ]: false,
                [modulos.RESULTADOS     ]: false
            }
        };
    }

    /**
     * Verifica se o usuário está logado
     */
    async componentDidMount() {
        this._isMounted = true;
        //Observando caso a sessão expire
        LoginController.watchSessionExpired(this.handleLogout);
        const autenticado = await this.loginController.estaLogado();
        if (this._isMounted) {
            let perfilCliente = this.state.perfilCliente;
            let permissoes = this.state.permissoes;
            let permiteCadastrarAdmin = this.state.permiteCadastrarAdmin;
            let empresa = this.state.empresa;
            let usuario = this.state.usuario;
            if (autenticado.sucesso) {
                permissoes = await this.permissaoController.permissoesUsuario();
                permiteCadastrarAdmin = await this.permissaoController.permiteCadastrarAdministradores();
                perfilCliente = perfilAcesso.CLIENTES.includes(await this.permissaoController.retornarPerfil());
                empresa = await this.permissaoController.retornarEmpresa();
                usuario = await this.permissaoController.retornarUsuario();
            }
            this.setState({ carregando: false, autenticado: autenticado.sucesso, permissoes, perfilCliente, permiteCadastrarAdmin, empresa, usuario });
        }
    }

    /**
     * Garante que o setState não vai ser chamado depois do componente ser desmontado
     */
    componentWillUnmount() {
        LoginController.unwatchSessionExpired(this.handleLogout);
        this._isMounted = false;
    }

    /**
     * Atualiza o estado da aplicação indicando que o usário está logado
     */
    async handleLogin() {
        const permissoes = await this.permissaoController.permissoesUsuario();
        const permiteCadastrarAdmin = await this.permissaoController.permiteCadastrarAdministradores();
        const perfilCliente = perfilAcesso.CLIENTES.includes(await this.permissaoController.retornarPerfil());
        const empresa = await this.permissaoController.retornarEmpresa();
        const usuario = await this.permissaoController.retornarUsuario();
        this.setState({ autenticado: true, permissoes, perfilCliente, permiteCadastrarAdmin, empresa, usuario });
    }
    
    /**
     * Atualiza o estado da aplicação indicando que o usuário não está mais logado
     */
    handleLogout() {
        //Limpando controller para evitar cache
        this.permissaoController = new PermissaoController();
        this.setState({ autenticado: false });
    }

    render() {
        const { carregando, autenticado, permissoes, permiteCadastrarAdmin, perfilCliente, usuario, empresa } = this.state;
        if (carregando) return <ThemeProvider theme={theme}><LoadingAppPage /></ThemeProvider>;

        const componenteHome = perfilCliente ? ResultadoFilterPage : DashboardPage;

        return (
            <div className="App">
                <ThemeProvider theme={theme}>
                    <BrowserRouter basename="/">
                        <Switch>
                            <ConditionalRoute path="/login"                 condition={!autenticado} component={LoginPage} redirect="/" childProps={{ onLogin: this.handleLogin }} />
                            <ConditionalRoute path="/recuperarSenha/:token" condition={!autenticado} component={ResetarSenhaPage} redirect="/" />
                            <ConditionalRoute path="/recuperarSenha"        condition={!autenticado} component={RecuperarSenhaPage} redirect="/" />
                            <ConditionalRoute path="/"                      component={componenteHome} exact condition={autenticado} redirect="/login" childProps={{ onLogout: this.handleLogout, usuario, empresa, permissoes }} />

                            <ConditionalRoute path="/administrador/:id"       component={AdministradorFormPage}  conditions={[ { condition: autenticado , redirect: '/login' }, { condition: (permissoes[modulos.ADMINISTRADORES] && permiteCadastrarAdmin), redirect: '/' } ]} childProps={{ onLogout: this.handleLogout, usuario, empresa, permissoes }} />
                            <ConditionalRoute path="/administrador"           component={AdministradorListPage}  conditions={[ { condition: autenticado , redirect: '/login' }, { condition: (permissoes[modulos.ADMINISTRADORES] && permiteCadastrarAdmin), redirect: '/' } ]} childProps={{ onLogout: this.handleLogout, usuario, empresa, permissoes }} />
                            
                            <PermissionRoute path="/empresa/:id"               component={EmpresaFormPage}        modulo={modulos.EMPRESAS}         autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                            <PermissionRoute path="/empresa"                   component={EmpresaListPage}        modulo={modulos.EMPRESAS}         autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                            <PermissionRoute path="/cliente/:id"               component={ClienteFormPage}        modulo={modulos.CLIENTES}         autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                            <PermissionRoute path="/cliente"                   component={ClienteListPage}        modulo={modulos.CLIENTES}         autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                            <PermissionRoute path="/avaliador/:id"             component={AvaliadorFormPage}      modulo={modulos.AVALIADORES}      autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                            <PermissionRoute path="/avaliador"                 component={AvaliadorListPage}      modulo={modulos.AVALIADORES}      autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                            <PermissionRoute path="/questionario/:id/copia"    component={QuestionarioFormPage}   modulo={modulos.QUESTIONARIOS}    autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                            <PermissionRoute path="/questionario/:id"          component={QuestionarioFormPage}   modulo={modulos.QUESTIONARIOS}    autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                            <PermissionRoute path="/questionario/"             component={QuestionarioListPage}   modulo={modulos.QUESTIONARIOS}    autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                            <PermissionRoute path="/avaliacao/add"             component={AvaliacaoAddPage}       modulo={modulos.AVALIACOES}       autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                            <PermissionRoute path="/avaliacao/resultado/"      component={ResultadoShowPage}      modulo={modulos.RESULTADOS}       autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                            <PermissionRoute path="/avaliacao/:id"             component={AvaliacaoEditPage}      modulo={modulos.AVALIACOES}       autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                            <PermissionRoute path="/avaliacao/"                component={AvaliacaoListPage}      modulo={modulos.AVALIACOES}       autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                            <PermissionRoute path="/resultado/"                component={ResultadoFilterPage}    modulo={modulos.RESULTADOS}       autenticado={autenticado} onLogout={this.handleLogout} usuario={usuario} empresa={empresa} permissoes={permissoes} />
                        </Switch>   
                    </BrowserRouter>
                </ThemeProvider>
            </div>
        );
    }
}

/**
 * Renderiza um componente específico para as rotas que dependem de uma permissão
 * @param {Object} props Propriedades do componente
 * @param {String} props.path URL da rota
 * @param {Object} props.component Componente renderizado se a rota for acessada
 * @param {Function} props.onLogout Método callback executado quando o usuário realizar o logout
 * @param {Object} props.usuario Dados do usuário autenticado
 * @param {Function} props.empresa Empresa relacionada ao usuário
 * @param {String} props.modulo Identificação do módulo referente à rota
 * @param {Boolean} props.autenticado Identifica se o usuário já está autenticado na aplicação
 * @param {Object} props.permissoes Objeto contendo as permissões do usuário
 */
function PermissionRoute({ path, component, onLogout, usuario, empresa, modulo, autenticado, permissoes }) {
    //Renderiza o componente repassando as propriedades necessárias
    return <ConditionalRoute 
        path={path} 
        component={component} 
        childProps={{ onLogout, usuario, empresa, permissoes }} 
        conditions={[
            { condition: autenticado       , redirect: '/login' },
            { condition: permissoes[modulo], redirect: '/'      },
        ]} 
    />;
}

PermissionRoute.propTypes = {
    /** URL da rota */
    path: PropTypes.string.isRequired,
    /** Componente renderizado se a rota for acessada */
    component: PropTypes.any.isRequired,
    /** Método callback executado quando o usuário realizar o logout */
    onLogout: PropTypes.func,
    /** Dados do usuário autenticado */
    usuario: PropTypes.object,
    /** Empresa relacionada ao usuário */
    empresa: PropTypes.object,
    /** Identificação do módulo referente à rota */
    modulo: PropTypes.string.isRequired,
    /** Identifica se o usuário já está autenticado na aplicação */
    autenticado: PropTypes.bool,
    /** Objeto contendo as permissões do usuário */
    permissoes: PropTypes.object
};

export default App;
